<template>
  <div v-show="searchStore.filtersOpen" class="content-filter-refinement">
    <skeleton-filter-loader v-if="loading" />
    <div v-else class="refinement-options">
      <!-- mobile actions -->
      <div class="content-refinement-actions mobile-filters">
        <div class="results-actions">
          <div
            v-if="showFilterCollections && features.FILTER_COLLECTIONS && entityType === 'workout'"
            class="action"
          >
            <filter-collection-form-button :entity-type="entityType" />
          </div>
          <div class="action">
            <icon-button
              type="button"
              icon="filter_alt_off"
              classes=""
              label="Close"
              :icons-outlined="false"
              :reduce="false"
              @click-action="closeFilters"
            />
          </div>
        </div>
      </div>

      <!-- filters -->
      <keyword-search
        v-if="showKeywords"
        @keyword-update="closeAndTriggerFiltering"
        @keyword-clear="triggerFiltering"
      />
      <user-tag-search
        v-if="showTags && features.TAGS && dropdownFilters && entityType !== 'notification'"
        :dropdown-filter="dropdownFilters.tags"
        :entity-type="entityType"
        :entity-category="entityCategory"
        @dropdown-change="triggerFiltering"
      />

      <!-- custom filters -->
      <slot />
    </div>

    <!-- overlay -->
    <teleport to="body">
      <div class="content-refinement-overlay" @click.stop="closeFilters"></div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
/**
 * Component handles the following:
 * - adding body class filter-open
 * - resize listener to force filters to open
 * - outside click handler to close filters on mobile
 */
import { onBeforeMount, onBeforeUnmount, watchEffect } from 'vue'
import { useSearchStore } from '@/stores/search'
import IconButton from '@/components/buttons/icon-button.vue'
import KeywordSearch from '@/components/search/keyword-search.vue'
import { bp } from '@/constants'
import { useSearchFilter } from '@/composables/search-filter'
import UserTagSearch from '@/components/search/user-tag-search.vue'
import SkeletonFilterLoader from '@/components/loaders/skeleton-filter-loader.vue'
import type { SearchTag } from '@/types/search-types'
import FilterCollectionFormButton from '@/components/buttons/filter-collections/filter-collection-form-button.vue'
import type { EntityType } from '@/types/search-types'

/** PROPS **/
withDefaults(
  defineProps<{
    loading?: boolean
    showKeywords?: boolean
    showTags?: boolean
    showFilterCollections?: boolean
    entityType: EntityType | 'notification'
    entityCategory?: '' | 'recipe'
  }>(),
  {
    loading: false,
    showKeywords: true,
    showTags: false,
    showFilterCollections: false,
    showVideoFilters: false,
    entityCategory: '',
  },
)

/** STATE **/
const searchStore = useSearchStore()

/** EMITS **/
const emit = defineEmits(['filter-update'])

/** COMPOSABLES **/
const { features, dropdownFilters, triggerFiltering } = useSearchFilter({ emit })

/** WATCHER **/
// side effect to update body class, better to do this here than in pinia
watchEffect(() => {
  if (searchStore.filtersOpen) {
    document.querySelector('body')?.classList.add('filters-open')
  } else {
    document.querySelector('body')?.classList.remove('filters-open')
  }
})

/** LIFECYCLE **/
// force open on large screens
onBeforeMount(() => {
  resizeToggle()
})

// clean up class addition
onBeforeUnmount(() => {
  document.querySelector('body')?.classList.remove('filters-open')
})

/** METHODS **/
function closeAndTriggerFiltering(tagItems: SearchTag) {
  // auto close on mobile only
  if (window.innerWidth <= bp.headerTablet) {
    closeFilters()
  }

  triggerFiltering(tagItems)
}

function closeFilters() {
  searchStore.setFiltersOpen(false)
}

// force open on larger screens
function resizeToggle() {
  if (window.innerWidth > bp.headerTablet) {
    searchStore.setFiltersOpen(true)
  }
}
</script>
